import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, LoadingSpinner, Avatar } from 'components/common';
import RichContent from 'components/common/RichContent';
import { MdOutlineForum } from 'react-icons/md';
import { LuReply } from 'react-icons/lu';
import PropTypes from 'prop-types';
import { escapeHtml } from 'helpers/utils';
import InteractionTime from './InteractionTime';
import ResponseAttachments from './ResponseAttachments';
import ResponseForm from './ResponseForm';

// Rich-text của một response: cùng bộ typography với description của topic, nhưng
// nhỏ hơn một bậc vì đây là câu trả lời lồng trong topic. Kích thước ảnh do
// RichContent quản lý (thumbnail + lightbox).
const CONTENT_CLS = [
  'tw-text-[0.875rem] tw-leading-relaxed tw-text-slate-700',
  '[&_p]:tw-mb-2 [&_p:last-child]:tw-mb-0',
  '[&_ul]:tw-pl-5 [&_ul]:tw-mb-2 [&_ul]:tw-list-disc',
  '[&_ol]:tw-pl-5 [&_ol]:tw-mb-2 [&_ol]:tw-list-decimal',
  '[&_li]:tw-mb-0.5',
  '[&_a]:tw-text-primary-600 [&_a]:tw-underline hover:[&_a]:tw-text-primary-800',
  '[&_blockquote]:tw-border-l-2 [&_blockquote]:tw-border-slate-300 [&_blockquote]:tw-pl-3 [&_blockquote]:tw-italic [&_blockquote]:tw-text-slate-500',
  '[&_pre]:tw-bg-slate-100 [&_pre]:tw-rounded [&_pre]:tw-p-2 [&_pre]:tw-overflow-x-auto [&_pre]:tw-text-xs',
  '[&_code]:tw-bg-slate-100 [&_code]:tw-px-1 [&_code]:tw-rounded-sm [&_code]:tw-text-xs',
  '[&_table]:tw-w-full [&_table]:tw-border-collapse [&_table]:tw-my-2',
  '[&_th]:tw-border [&_th]:tw-border-slate-300 [&_th]:tw-bg-slate-100 [&_th]:tw-px-2 [&_th]:tw-py-1 [&_th]:tw-text-xs',
  '[&_td]:tw-border [&_td]:tw-border-slate-200 [&_td]:tw-px-2 [&_td]:tw-py-1 [&_td]:tw-text-xs',
].join(' ');

// Thread dài thì chỉ hiện vài reply mới nhất; người đọc cần câu kết luận, các
// trao đổi trước đó mở ra khi cần.
const VISIBLE_REPLIES = 2;
const SNIPPET_LENGTH = 120;

const htmlToSnippet = (html) => {
  if (!html) return '';
  const text = typeof window !== 'undefined' && typeof window.DOMParser === 'function'
    ? new window.DOMParser().parseFromString(html, 'text/html').body.textContent || ''
    : html.replace(/<[^>]*>/g, ' ');
  const squished = text.replace(/\s+/g, ' ').trim();
  return squished.length > SNIPPET_LENGTH ? `${squished.slice(0, SNIPPET_LENGTH)}…` : squished;
};

// Chấp nhận cả bản ghi phẳng lẫn dạng { attributes } của JSON:API.
const normalizeResponse = (item, index) => {
  const response = item?.attributes || item;
  if (!response || typeof response !== 'object') return null;

  return {
    id: response.id || `temp-${index}`,
    parent_id: response.parent_id || null,
    content: response.content || '',
    author: {
      name: response.author?.name || 'Anonymous',
      avatar: response.author?.avatar || null,
    },
    created_at: response.created_at || null,
    documents: Array.isArray(response.documents) ? response.documents : [],
    replies: Array.isArray(response.replies)
      ? response.replies.map(normalizeResponse).filter(Boolean)
      : [],
  };
};

// ── Một response ──────────────────────────────────────────────────────────────
// Bố cục kiểu comment thread: avatar nhỏ bên trái, dòng "ai · khi nào" ngay trên
// nội dung. Trước đây tên và thời gian nằm ở chân card sau nội dung, ngăn cách
// bằng dấu "|", nên đọc xong mới biết của ai.
const ResponseItem = ({ response, isReply, onReply, children }) => (
  // Thẻ trắng nổi trên nền xám của khối Responses: response phải nhìn ra ngay
  // là một tầng khác với nội dung topic, không phải phần tiếp theo của nó.
  // Reply nằm trong thẻ của response gốc nên không cần khung riêng.
  <li
    className={isReply
      ? 'tw-relative tw-flex tw-gap-2 tw-py-2 tw-list-none'
      : 'tw-relative tw-flex tw-gap-2.5 tw-p-3 tw-mb-2 last:tw-mb-0 tw-bg-white tw-rounded-lg tw-ring-1 tw-ring-slate-200/70 tw-shadow-sm'}
  >
    {/* Mấu nối vào đường kẻ dọc của thread */}
    {!isReply && <span aria-hidden className="tw-absolute tw--left-3 tw-top-6 tw-w-3 tw-h-px tw-bg-slate-200" />}

    <Avatar
      src={response.author?.avatar}
      name={response.author?.name || 'Anonymous'}
      size={isReply ? 'xs' : 'sm'}
      className="tw-flex-shrink-0 tw-mt-0.5"
    />

    <div className="tw-min-w-0 tw-flex-grow">
      <div className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-2 tw-mb-1">
        <span className="tw-text-xs tw-font-semibold tw-text-slate-700">
          {response.author?.name || 'Anonymous'}
        </span>
        <InteractionTime date={response.created_at} fallback="Just now" className="tw-text-[11px] tw-text-slate-400" />
      </div>

      <RichContent html={response.content} className={CONTENT_CLS} collapsible collapsedHeight={isReply ? 240 : 360} />

      <ResponseAttachments documents={response.documents} />

      {onReply && (
        <button
          type="button"
          onClick={() => onReply(response)}
          className="tw-mt-1.5 tw-inline-flex tw-items-center tw-gap-1 tw-p-0 tw-border-0 tw-bg-transparent tw-text-[11px] tw-font-semibold tw-text-slate-500 hover:tw-text-primary-700 tw-cursor-pointer tw-transition-colors"
        >
          <LuReply size={12} />
          Reply
        </button>
      )}

      {children}
    </div>
  </li>
);
ResponseItem.propTypes = {
  response: PropTypes.object.isRequired,
  isReply: PropTypes.bool,
  onReply: PropTypes.func,
  children: PropTypes.node,
};

// ── Một thread: response gốc + các reply + form trả lời inline ────────────────
const ResponseThread = ({ topic, root, allowReply, replyTarget, onReply, onCancelReply }) => {
  const [showAllReplies, setShowAllReplies] = useState(false);
  const formRef = useRef(null);

  const { replies } = root;
  const hiddenCount = showAllReplies ? 0 : Math.max(replies.length - VISIBLE_REPLIES, 0);
  const visibleReplies = hiddenCount > 0 ? replies.slice(-VISIBLE_REPLIES) : replies;
  // Topic bị đóng trong lúc đang gõ thì đóng luôn form — gửi lên chỉ nhận 422.
  const isReplyingHere = allowReply && replyTarget?.rootId === root.id;

  useEffect(() => {
    if (!isReplyingHere) return;
    requestAnimationFrame(() => formRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' }));
  }, [isReplyingHere, replyTarget?.targetId]);

  // Trả lời vào một reply: gửi id của CHÍNH reply đó — server làm phẳng về gốc
  // (thread một cấp) nhưng vẫn biết mail cho ai và trích dẫn câu nào.
  const handleReply = useCallback((target) => onReply(root, target), [onReply, root]);
  const replyHandler = allowReply ? handleReply : undefined;

  return (
    <ResponseItem response={root} onReply={replyHandler}>
      {(replies.length > 0 || isReplyingHere) && (
        <div className="tw-mt-2 tw-pl-3 tw-border-0 tw-border-l-2 tw-border-solid tw-border-slate-100">
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setShowAllReplies(true)}
              className="tw-px-0 tw-py-1 tw-border-0 tw-bg-transparent tw-text-[11px] tw-font-semibold tw-text-primary-600 hover:tw-text-primary-800 tw-cursor-pointer"
            >
              View {hiddenCount} earlier {hiddenCount === 1 ? 'reply' : 'replies'}
            </button>
          )}

          {visibleReplies.length > 0 && (
            <ul className="tw-m-0 tw-p-0 tw-divide-y tw-divide-solid tw-divide-slate-100">
              {visibleReplies.map((reply) => (
                <ResponseItem key={reply.id} response={reply} isReply onReply={replyHandler} />
              ))}
            </ul>
          )}

          {isReplyingHere && (
            <div ref={formRef} className="tw-pt-2">
              <ResponseForm
                // Đổi đối tượng trả lời thì dựng form mới để nạp lại @tên.
                key={`${root.id}-${replyTarget.targetId}`}
                open
                compact
                topic={topic}
                parentId={replyTarget.targetId}
                replyTo={{ name: replyTarget.name, snippet: replyTarget.snippet }}
                initialContent={replyTarget.initialContent}
                onCancel={onCancelReply}
                onSuccess={onCancelReply}
              />
            </div>
          )}
        </div>
      )}
    </ResponseItem>
  );
};
ResponseThread.propTypes = {
  topic: PropTypes.object.isRequired,
  root: PropTypes.object.isRequired,
  allowReply: PropTypes.bool,
  replyTarget: PropTypes.object,
  onReply: PropTypes.func.isRequired,
  onCancelReply: PropTypes.func.isRequired,
};

// ── Danh sách response ────────────────────────────────────────────────────────
const ResponseCard = ({ topic, responses, isResponseLoading, onLoadMore, allowReply }) => {
  const { data, paginate } = responses || {};
  // Mỗi topic chỉ mở một form trả lời inline tại một thời điểm.
  const [replyTarget, setReplyTarget] = useState(null);

  const responsesData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.filter(Boolean).map(normalizeResponse).filter(Boolean);
  }, [data]);

  const { total_rows: totalRows = 0, next_page: hasNextPage = null } = paginate || {};

  const handleLoadMoreClick = useCallback(() => {
    if (topic?.id) onLoadMore(topic.id);
  }, [topic?.id, onLoadMore]);

  const handleReply = useCallback((root, target) => {
    const isNested = target.id !== root.id;
    setReplyTarget({
      rootId: root.id,
      targetId: target.id,
      name: target.author?.name,
      snippet: htmlToSnippet(target.content),
      initialContent: isNested && target.author?.name ? `<p><strong>@${escapeHtml(target.author.name)}</strong>&nbsp;</p>` : '',
    });
  }, []);

  const handleCancelReply = useCallback(() => setReplyTarget(null), []);

  // Chỉ thay cả danh sách bằng spinner khi chưa có gì để hiện. "Load more" /
  // refresh mà unmount danh sách thì form trả lời inline đang gõ dở mất sạch.
  if (isResponseLoading && responsesData.length === 0) {
    return (
      <div className="tw-text-center tw-py-8">
        <LoadingSpinner size="sm" message="Loading responses..." />
      </div>
    );
  }

  if (responsesData.length === 0) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-8 tw-text-center">
        <MdOutlineForum size={22} className="tw-text-slate-300 tw-mb-1.5" />
        <p className="tw-text-xs tw-text-slate-400 tw-mb-0">No responses yet. Be the first to respond.</p>
      </div>
    );
  }

  return (
    <>
      {/* Thụt vào một bậc + đường kẻ dọc: nhìn là biết đây là các câu trả lời
          lồng trong topic, chứ không phải nội dung tiếp theo của chính topic. */}
      <ul className="tw-list-none tw-m-0 tw-pl-3 tw-pr-0 tw-py-0 tw-ml-3 tw-border-0 tw-border-l-2 tw-border-solid tw-border-slate-200">
        {responsesData.map((root) => (
          <ResponseThread
            key={root.id}
            topic={topic}
            root={root}
            allowReply={allowReply}
            replyTarget={replyTarget}
            onReply={handleReply}
            onCancelReply={handleCancelReply}
          />
        ))}
      </ul>

      {/* total_rows đếm response GỐC — reply nằm sẵn trong từng gốc. */}
      {hasNextPage && (
        <div className="tw-text-center tw-mt-3">
          <Button variant="ghost" size="sm" className="tw-text-primary-600" onClick={handleLoadMoreClick}>
            Load more responses ({Math.max(totalRows - responsesData.length, 0)} remaining)
          </Button>
        </div>
      )}
    </>
  );
};

ResponseCard.propTypes = {
  topic: PropTypes.object.isRequired,
  isResponseLoading: PropTypes.bool.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  responses: PropTypes.object,
  /** Cho phép trả lời từng response — topic.permissions.allow_create_response */
  allowReply: PropTypes.bool,
};

ResponseCard.defaultProps = {
  allowReply: false,
};

export default ResponseCard;
